import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { MapPin, Globe, Scale, Ruler, Calendar, ChevronDown, Check } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';

// Country names in multiple languages
const COUNTRY_NAMES = {
  'US': { en: 'United States', it: 'Stati Uniti', es: 'Estados Unidos', pt: 'Estados Unidos', de: 'Vereinigte Staaten', fr: 'États-Unis' },
  'GB': { en: 'United Kingdom', it: 'Regno Unito', es: 'Reino Unido', pt: 'Reino Unido', de: 'Vereinigtes Königreich', fr: 'Royaume-Uni' },
  'IT': { en: 'Italy', it: 'Italia', es: 'Italia', pt: 'Itália', de: 'Italien', fr: 'Italie' },
  'ES': { en: 'Spain', it: 'Spagna', es: 'España', pt: 'Espanha', de: 'Spanien', fr: 'Espagne' },
  'DE': { en: 'Germany', it: 'Germania', es: 'Alemania', pt: 'Alemanha', de: 'Deutschland', fr: 'Allemagne' },
  'FR': { en: 'France', it: 'Francia', es: 'Francia', pt: 'França', de: 'Frankreich', fr: 'France' },
  'BR': { en: 'Brazil', it: 'Brasile', es: 'Brasil', pt: 'Brasil', de: 'Brasilien', fr: 'Brésil' },
  'PT': { en: 'Portugal', it: 'Portogallo', es: 'Portugal', pt: 'Portugal', de: 'Portugal', fr: 'Portugal' },
  'MX': { en: 'Mexico', it: 'Messico', es: 'México', pt: 'México', de: 'Mexiko', fr: 'Mexique' },
  'AR': { en: 'Argentina', it: 'Argentina', es: 'Argentina', pt: 'Argentina', de: 'Argentinien', fr: 'Argentine' },
  'AU': { en: 'Australia', it: 'Australia', es: 'Australia', pt: 'Austrália', de: 'Australien', fr: 'Australie' },
  'CA': { en: 'Canada', it: 'Canada', es: 'Canadá', pt: 'Canadá', de: 'Kanada', fr: 'Canada' },
};

const FLAG_EMOJIS = {
  'US': '🇺🇸', 'GB': '🇬🇧', 'IT': '🇮🇹', 'ES': '🇪🇸', 'DE': '🇩🇪', 'FR': '🇫🇷',
  'BR': '🇧🇷', 'PT': '🇵🇹', 'MX': '🇲🇽', 'AR': '🇦🇷', 'AU': '🇦🇺', 'CA': '🇨🇦'
};

// Translations for this page
const TEXTS = {
  title: {
    en: 'Welcome!',
    it: 'Benvenuto!',
    es: '¡Bienvenido!',
    pt: 'Bem-vindo!',
    de: 'Willkommen!',
    fr: 'Bienvenue!'
  },
  subtitle: {
    en: 'Let\'s set up your preferences',
    it: 'Impostiamo le tue preferenze',
    es: 'Configuremos tus preferencias',
    pt: 'Vamos configurar suas preferências',
    de: 'Lass uns deine Einstellungen konfigurieren',
    fr: 'Configurons vos préférences'
  },
  detected: {
    en: 'Detected location',
    it: 'Posizione rilevata',
    es: 'Ubicación detectada',
    pt: 'Localização detectada',
    de: 'Erkannter Standort',
    fr: 'Localisation détectée'
  },
  language: {
    en: 'Language',
    it: 'Lingua',
    es: 'Idioma',
    pt: 'Idioma',
    de: 'Sprache',
    fr: 'Langue'
  },
  unitSystem: {
    en: 'Unit System',
    it: 'Sistema di misura',
    es: 'Sistema de unidades',
    pt: 'Sistema de unidades',
    de: 'Einheitensystem',
    fr: 'Système d\'unités'
  },
  metric: {
    en: 'Metric',
    it: 'Metrico',
    es: 'Métrico',
    pt: 'Métrico',
    de: 'Metrisch',
    fr: 'Métrique'
  },
  imperial: {
    en: 'Imperial',
    it: 'Imperiale',
    es: 'Imperial',
    pt: 'Imperial',
    de: 'Imperial',
    fr: 'Impérial'
  },
  metricDesc: {
    en: 'kg, cm, km',
    it: 'kg, cm, km',
    es: 'kg, cm, km',
    pt: 'kg, cm, km',
    de: 'kg, cm, km',
    fr: 'kg, cm, km'
  },
  imperialDesc: {
    en: 'lbs, ft, mi',
    it: 'lbs, ft, mi',
    es: 'lbs, ft, mi',
    pt: 'lbs, ft, mi',
    de: 'lbs, ft, mi',
    fr: 'lbs, ft, mi'
  },
  dateFormat: {
    en: 'Date Format',
    it: 'Formato data',
    es: 'Formato de fecha',
    pt: 'Formato de data',
    de: 'Datumsformat',
    fr: 'Format de date'
  },
  confirm: {
    en: 'Continue',
    it: 'Continua',
    es: 'Continuar',
    pt: 'Continuar',
    de: 'Weiter',
    fr: 'Continuer'
  }
};

export default function GeoSettingsScreen({ onComplete }) {
  const navigate = useNavigate();
  const { language, setLanguage, geoData } = useLanguage();
  const { updateSettings } = useUnits();
  const { user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [unitSystem, setUnitSystem] = useState('metric'); // 'metric' or 'imperial'
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geoLoading, setGeoLoading] = useState(true);
  const [geoInitialized, setGeoInitialized] = useState(false);

  // Initialize based on geo detection - wait for geoData with timeout
  // Initialize based on geo detection. Priority order:
  //   1. Live geo-IP detection (most accurate when reachable)
  //   2. `user.country` already on the DB record — set by the backend
  //      from the phone-number international prefix at signup. This
  //      fallback is what makes a Portuguese user signing up via
  //      `+351...` see the Portuguese flag immediately, even if the
  //      geo-IP service is blocked, slow, or behind a VPN.
  //   3. Generic IT default (legacy behaviour) only as a last resort.
  useEffect(() => {
    if (geoInitialized) return;

    const country = geoData?.countryCode || user?.country;
    if (country) {
      if (['US', 'LR', 'MM', 'GB'].includes(country)) {
        setUnitSystem('imperial');
      }
      if (country === 'US') {
        setDateFormat('MM/DD/YYYY');
      }
      setGeoLoading(false);
      setGeoInitialized(true);
    }
  }, [geoData, user?.country, geoInitialized]);
  
  // Timeout for geo loading - don't wait forever
  useEffect(() => {
    const timer = setTimeout(() => {
      if (geoLoading) {
        setGeoLoading(false);
        setGeoInitialized(true);
      }
    }, 3000); // 3 second timeout
    
    return () => clearTimeout(timer);
  }, [geoLoading]);

  const handleLanguageChange = (langCode) => {
    setSelectedLanguage(langCode);
    setLanguage(langCode);
    setShowLanguageDropdown(false);
  };

  const handleConfirm = async () => {
    setSaving(true);
    
    try {
      const settings = unitSystem === 'imperial' ? {
        weight_unit: 'lbs',
        height_unit: 'ft',
        distance_unit: 'mi',
        date_format: dateFormat,
        time_format: '12h',
        first_day_of_week: dateFormat === 'MM/DD/YYYY' ? 'sunday' : 'monday',
        decimal_separator: 'period',
        detected_country: geoData?.countryCode || null
      } : {
        weight_unit: 'kg',
        height_unit: 'cm',
        distance_unit: 'km',
        date_format: dateFormat,
        time_format: '24h',
        first_day_of_week: 'monday',
        decimal_separator: 'comma',
        detected_country: geoData?.countryCode || null
      };
      
      // Save to backend
      await axios.put(`${API_URL}/api/users/me`, settings, { withCredentials: true });
      
      // Update local settings
      await updateSettings(settings);
      
      // Mark as confirmed
      localStorage.setItem('geo_settings_confirmed', 'true');
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
      
      // Call completion callback or navigate
      if (onComplete) {
        onComplete();
      } else {
        navigate('/app/nuovo-piano');
      }
      
    } catch (err) {
      console.error('Failed to save settings:', err);
      // Continue anyway
      if (onComplete) {
        onComplete();
      } else {
        navigate('/app/nuovo-piano');
      }
    } finally {
      setSaving(false);
    }
  };

  const t = (key) => TEXTS[key]?.[selectedLanguage] || TEXTS[key]?.['en'] || key;
  
  // Country display priority: geo-IP > user.country (set from phone
  // prefix at signup) > IT fallback. Same priority as the imperial/
  // date-format pre-fill above so flag and units are always coherent.
  const countryCode = geoData?.countryCode || user?.country || 'IT';
  const countryName = COUNTRY_NAMES[countryCode]?.[selectedLanguage] || geoData?.country || countryCode;
  const flag = FLAG_EMOJIS[countryCode] || '🌍';
  
  const currentLang = LANGUAGES.find(l => l.code === selectedLanguage) || LANGUAGES[0];

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isDark ? 'bg-[#0a0f1a]' : 'bg-gradient-to-br from-[#1d3557] to-[#457b9d]'}`} style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -right-1/4 w-96 h-96 bg-[#E8334A]/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-1/4 -left-1/4 w-96 h-96 bg-[#457b9d]/30 rounded-full blur-3xl"></div>
      </div>

      {/* Loading State */}
      {geoLoading && (
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/70">{t('detected')}...</p>
        </div>
      )}

      {/* Content - shown after geo loaded or timeout */}
      {!geoLoading && (
        <div className="relative z-10 flex-1 flex flex-col px-5 py-4 overflow-y-auto justify-center">
        {/* Header with Logo */}
        <div className="text-center mb-4">
          <img 
            src="/logo_white.png" 
            alt="Slimmy" 
            className="h-10 mx-auto mb-2"
          />
          <h1 className="text-xl font-bold text-white mb-0.5">{t('title')}</h1>
          <p className="text-white/70 text-xs">{t('subtitle')}</p>
        </div>

        {/* Detected Location Card */}
        {geoData?.countryCode && (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2.5 mb-3 border border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center">
                <MapPin className="w-3.5 h-3.5 text-white/70" />
              </div>
              <div>
                <p className="text-white/50 text-[10px] uppercase tracking-wider">{t('detected')}</p>
                <p className="text-white font-medium text-sm flex items-center gap-1.5">
                  <span>{flag}</span> {countryName}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Language Selector */}
        <div className="mb-3">
          <label className="text-white/70 text-xs font-medium mb-1.5 block flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" />
            {t('language')}
          </label>
          <div className="relative">
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className="w-full bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3 flex items-center justify-between text-white"
            >
              <span className="flex items-center gap-2">
                <span className="text-xl">{currentLang.flag}</span>
                <span className="font-medium text-sm">{currentLang.name}</span>
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showLanguageDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#1d3557] border border-white/20 rounded-xl overflow-hidden shadow-xl z-20 max-h-48 overflow-y-auto">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`w-full p-2.5 flex items-center gap-2 hover:bg-white/10 transition-colors ${
                      selectedLanguage === lang.code ? 'bg-white/10' : ''
                    }`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className="text-white font-medium text-sm flex-1 text-left">{lang.name}</span>
                    {selectedLanguage === lang.code && (
                      <Check className="w-4 h-4 text-[#E8334A]" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Unit System Toggle */}
        <div className="mb-3">
          <label className="text-white/70 text-xs font-medium mb-1.5 block flex items-center gap-1.5">
            <Scale className="w-3.5 h-3.5" />
            {t('unitSystem')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setUnitSystem('metric')}
              className={`p-3 rounded-xl border-2 transition-all ${
                unitSystem === 'metric'
                  ? 'bg-[#E8334A] border-[#E8334A] text-white'
                  : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <Ruler className="w-4 h-4" />
                <span className="font-semibold text-sm">{t('metric')}</span>
              </div>
              <p className={`text-xs ${unitSystem === 'metric' ? 'text-white/80' : 'text-white/50'}`}>
                {t('metricDesc')}
              </p>
            </button>
            <button
              onClick={() => setUnitSystem('imperial')}
              className={`p-3 rounded-xl border-2 transition-all ${
                unitSystem === 'imperial'
                  ? 'bg-[#E8334A] border-[#E8334A] text-white'
                  : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
              }`}
            >
              <div className="flex items-center justify-center gap-1.5 mb-0.5">
                <Scale className="w-4 h-4" />
                <span className="font-semibold text-sm">{t('imperial')}</span>
              </div>
              <p className={`text-xs ${unitSystem === 'imperial' ? 'text-white/80' : 'text-white/50'}`}>
                {t('imperialDesc')}
              </p>
            </button>
          </div>
        </div>

        {/* Date Format Toggle */}
        <div className="mb-4">
          <label className="text-white/70 text-xs font-medium mb-1.5 block flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {t('dateFormat')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setDateFormat('DD/MM/YYYY')}
              className={`p-2.5 rounded-xl border-2 transition-all ${
                dateFormat === 'DD/MM/YYYY'
                  ? 'bg-[#E8334A] border-[#E8334A] text-white'
                  : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
              }`}
            >
              <span className="font-medium text-sm">DD/MM/YYYY</span>
              <p className={`text-[10px] mt-0.5 ${dateFormat === 'DD/MM/YYYY' ? 'text-white/80' : 'text-white/50'}`}>
                31/12/2025
              </p>
            </button>
            <button
              onClick={() => setDateFormat('MM/DD/YYYY')}
              className={`p-2.5 rounded-xl border-2 transition-all ${
                dateFormat === 'MM/DD/YYYY'
                  ? 'bg-[#E8334A] border-[#E8334A] text-white'
                  : 'bg-white/5 border-white/20 text-white/70 hover:bg-white/10'
              }`}
            >
              <span className="font-medium text-sm">MM/DD/YYYY</span>
              <p className={`text-[10px] mt-0.5 ${dateFormat === 'MM/DD/YYYY' ? 'text-white/80' : 'text-white/50'}`}>
                12/31/2025
              </p>
            </button>
          </div>
        </div>

        {/* Confirm Button */}
        <button
          onClick={handleConfirm}
          disabled={saving}
          className="w-full py-3.5 rounded-2xl bg-[#E8334A] text-white font-semibold text-base flex items-center justify-center gap-2 hover:bg-[#d62d43] transition-colors disabled:opacity-50 shadow-lg shadow-[#E8334A]/30 mt-2"
          data-testid="geo-settings-confirm-btn"
        >
          {saving ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Check className="w-4 h-4" />
              {t('confirm')}
            </>
          )}
        </button>
      </div>
      )}
    </div>
  );
}
